import React, { useEffect, useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createPaymentRequest } from "../../api/createPaymentRequest";
import { PaymentRequestRecipient } from "../../types/paymentRequest";

type EnrollmentRow = {
  enrollmentId: number;
  attendeeName: string;
  amount: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSent: () => void;
  enrollments: EnrollmentRow[];
  /** Prefill — one row per distinct checkout contact in the selection. */
  defaultRecipients?: PaymentRequestRecipient[];
};

const emptyRecipient: PaymentRequestRecipient = { email: "", name: "" };

export function SendPaymentRequestModal({
  open,
  onClose,
  onSent,
  enrollments,
  defaultRecipients,
}: Props) {
  const [recipients, setRecipients] = useState<PaymentRequestRecipient[]>([
    emptyRecipient,
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setRecipients(
        defaultRecipients && defaultRecipients.length > 0
          ? defaultRecipients
          : [emptyRecipient],
      );
      setError(null);
    }
  }, [open, defaultRecipients]);

  const total = enrollments.reduce((acc, e) => acc + Number(e.amount), 0);

  const updateRecipient = (
    index: number,
    field: keyof PaymentRequestRecipient,
    value: string,
  ) => {
    setRecipients((prev) =>
      prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)),
    );
  };

  const addRecipient = () =>
    setRecipients((prev) => [...prev, { ...emptyRecipient }]);

  const removeRecipient = (index: number) =>
    setRecipients((prev) => prev.filter((_, i) => i !== index));

  const recipientsValid =
    recipients.length > 0 &&
    recipients.every(
      (r) => r.email.trim().length > 0 && r.name.trim().length > 0,
    );
  const canSubmit = !submitting && enrollments.length > 0 && recipientsValid;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await createPaymentRequest({
        enrollmentIds: enrollments.map((e) => e.enrollmentId),
        recipients: recipients.map((r) => ({
          email: r.email.trim(),
          name: r.name.trim(),
        })),
      });
      onSent();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not send payment link.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog onOpenChange={(o) => (!o ? onClose() : null)} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send payment link</DialogTitle>
          <DialogDescription>
            One secure link, emailed to everyone below. Whoever pays first
            covers the full amount.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-md border border-gray-200 dark:border-slate-700 divide-y divide-gray-200 dark:divide-slate-700">
            {enrollments.map((e) => (
              <div
                className="flex justify-between px-3 py-2 text-sm"
                key={e.enrollmentId}
              >
                <span className="text-gray-900 dark:text-white">
                  {e.attendeeName}
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  ${Number(e.amount).toFixed(2)}
                </span>
              </div>
            ))}
            <div className="flex justify-between px-3 py-2 text-sm font-medium">
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Recipients</Label>
              <Button
                className="h-7 text-xs"
                onClick={addRecipient}
                size="sm"
                type="button"
                variant="ghost"
              >
                <Plus className="h-3 w-3 mr-1" /> Add recipient
              </Button>
            </div>
            {recipients.map((r, index) => (
              <div className="flex items-start gap-2" key={index}>
                <div className="flex-1 space-y-2">
                  <Input
                    onChange={(e) =>
                      updateRecipient(index, "name", e.target.value)
                    }
                    placeholder="Name"
                    value={r.name}
                  />
                  <Input
                    onChange={(e) =>
                      updateRecipient(index, "email", e.target.value)
                    }
                    placeholder="Email"
                    type="email"
                    value={r.email}
                  />
                </div>
                {recipients.length > 1 ? (
                  <Button
                    className="h-9 w-9 flex-shrink-0"
                    onClick={() => removeRecipient(index)}
                    size="icon"
                    type="button"
                    variant="ghost"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            ))}
          </div>

          {error ? (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          ) : null}
        </div>

        <DialogFooter>
          <Button onClick={onClose} type="button" variant="outline">
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={handleSubmit} type="button">
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…
              </>
            ) : (
              "Send link"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
